import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/utils';

interface EurdEntryDetail {
  id: string;
  activeSubstance: string;
  procedure: string | null;
  dataLockPoint: string;
  frequencyMonths: number;
  nextDueDate: string;
  psurRequired: boolean;
  notes: string | null;
  lastSyncedAt: string;
  updatedAt: string;
  effectiveDueDate: string;
  override: {
    nextDueDate?: string;
    notes?: string;
    updatedAt?: string;
  } | null;
}

interface RegulatoryAlert {
  id: string;
  matchedSubstances: string[];
  matchedProducts: string[];
  status: string;
  update: { headline: string; source: string; publishedAt: string | null } | null;
}

const EURD_PUBLIC_URL =
  'https://www.ema.europa.eu/en/documents/other/list-european-union-reference-dates-eurd-list_en.xlsx';

function frequencyLabel(months: number): string {
  if (months === 6) return '6-monthly';
  if (months === 12) return 'Yearly';
  if (months === 36) return '3-yearly';
  if (months === 60) return '5-yearly';
  return `${months}-monthly`;
}

export function EurdEntryDetailPage() {
  const { id } = useParams();

  const entry = useQuery<EurdEntryDetail>({
    queryKey: ['eurd', id],
    queryFn: async () => (await api.get(`/api/eurd/${id}`)).data,
    enabled: Boolean(id),
  });

  // Cross-reference: any TenantRegulatoryAlert whose RegulatoryUpdate carries
  // this active substance is shown as a "linked product" pointer.
  const alerts = useQuery({
    queryKey: ['reg-alerts', 'for-eurd', entry.data?.activeSubstance],
    queryFn: async () => {
      try {
        const res = await api.get('/api/reg-intel/alerts?pageSize=200');
        const all: RegulatoryAlert[] = res.data?.items ?? [];
        if (!entry.data) return [];
        const sub = entry.data.activeSubstance.toLowerCase();
        return all.filter((a) =>
          a.matchedSubstances.some((s) => s.toLowerCase() === sub),
        );
      } catch {
        return [];
      }
    },
    enabled: Boolean(entry.data?.activeSubstance),
  });

  if (entry.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading...</div>;
  }
  if (entry.isError || !entry.data) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/eurd">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to EURD list
          </Link>
        </Button>
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            EURD entry not found.
          </CardContent>
        </Card>
      </div>
    );
  }

  const e = entry.data;
  const linkedAlerts: RegulatoryAlert[] = alerts.data ?? [];

  const now = Date.now();
  const due = new Date(e.effectiveDueDate).getTime();
  const days = Math.round((due - now) / 86_400_000);
  const dueBadge =
    days < 0
      ? { label: `Overdue ${Math.abs(days)} days`, variant: 'destructive' as const }
      : days <= 30
        ? { label: `Due in ${days} days`, variant: 'warning' as const }
        : { label: `Due in ${days} days`, variant: 'secondary' as const };

  return (
    <div className="space-y-4">
      <Button asChild variant="ghost" size="sm" className="-ml-2">
        <Link to="/eurd">
          <ArrowLeft className="mr-2 h-4 w-4" /> EURD list
        </Link>
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl">{e.activeSubstance}</h1>
          <p className="text-sm text-muted-foreground">
            {e.procedure ?? 'EU Reference Date entry'} · synced {formatDate(e.lastSyncedAt)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={dueBadge.variant}>{dueBadge.label}</Badge>
          <Button asChild variant="outline" size="sm">
            <a href={EURD_PUBLIC_URL} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-1.5 h-4 w-4" /> EMA EURD List
            </a>
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-primary" /> Cycle
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Active substance" value={e.activeSubstance} />
            <Row label="Procedure" value={e.procedure ?? '-'} />
            <Row label="Data Lock Point (DLP)" value={formatDate(e.dataLockPoint)} />
            <Row label="Frequency" value={frequencyLabel(e.frequencyMonths)} />
            <Row label="Next due (EURD)" value={formatDate(e.nextDueDate)} />
            <Row
              label="Effective due"
              value={
                <span className="font-medium">
                  {formatDate(e.effectiveDueDate)}
                  {e.override?.nextDueDate && (
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      (tenant override)
                    </span>
                  )}
                </span>
              }
            />
            <Row
              label="PSUR required"
              value={e.psurRequired ? 'Yes' : 'No'}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Source + provenance</CardTitle>
            <CardDescription>
              Synced from the EU Reference Dates list maintained by EMA + national authorities.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Source" value="EMA EURD List" />
            <Row
              label="Source URL"
              value={
                <a
                  href={EURD_PUBLIC_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline-offset-2 hover:underline"
                >
                  ema.europa.eu/en/documents/other/list-european-union-reference-dates-eurd-list_en.xlsx
                </a>
              }
            />
            <Row label="Last synced" value={formatDate(e.lastSyncedAt)} />
            <Row label="Updated" value={formatDate(e.updatedAt)} />
            {e.notes && (
              <div className="mt-2 rounded-md border bg-muted/40 p-2 text-xs">
                {e.notes}
              </div>
            )}
            {e.override?.notes && (
              <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
                <div className="font-semibold">Tenant override note</div>
                <div>{e.override.notes}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Linked products + alerts</CardTitle>
          <CardDescription>
            Reg-intel alerts whose update mentions this active substance.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {linkedAlerts.length === 0 ? (
            <div className="text-xs text-muted-foreground">
              No reg-intel alerts currently link to {e.activeSubstance}.
            </div>
          ) : (
            <ul className="space-y-2">
              {linkedAlerts.map((a) => (
                <li
                  key={a.id}
                  className="flex items-start justify-between gap-3 rounded-md border p-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm">
                      {a.update?.headline ?? '-'}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{a.update?.source ?? '-'}</span>
                      {a.update?.publishedAt && (
                        <>
                          <span>·</span>
                          <span>{formatDate(a.update.publishedAt)}</span>
                        </>
                      )}
                      {a.matchedProducts.length > 0 && (
                        <>
                          <span>·</span>
                          <span>{a.matchedProducts.join(', ')}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <Badge variant="outline">{a.status.replace(/_/g, ' ')}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b py-1.5 last:border-0 last:pb-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right text-sm">{value}</span>
    </div>
  );
}
